import { useState, useEffect, useRef } from 'react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { 
  Plus, X, Check, Tag as TagIcon, Loader2 
} from 'lucide-react';
import { toast } from 'sonner';
import { usePermissions } from '../context/PermissionsContext';
import { logger } from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default tag colors
const TAG_COLORS = [
  '#EF4444', // Red
  '#F97316', // Orange
  '#F59E0B', // Amber
  '#84CC16', // Lime
  '#22C55E', // Green
  '#14B8A6', // Teal
  '#06B6D4', // Cyan
  '#3B82F6', // Blue
  '#6366F1', // Indigo
  '#8B5CF6', // Violet
  '#A855F7', // Purple
  '#EC4899', // Pink
  '#6B7280', // Gray
];

// Tag Badge Component
export function TagBadge({ tag, onRemove, small = false, showInactive = true }) {
  if (!tag) return null;
  
  const isInactive = !tag.is_active;
  
  if (isInactive && !showInactive) return null;
  
  return (
    <Badge
      className={`${small ? 'text-xs px-1.5 py-0' : 'text-xs px-2 py-0.5'} font-medium border ${isInactive ? 'opacity-50' : ''}`}
      style={{
        backgroundColor: `${tag.color}20`,
        borderColor: tag.color,
        color: tag.color
      }}
    >
      {tag.name}
      {isInactive && <span className="ml-1 text-[10px]">(inactive)</span>}
      {onRemove && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRemove(tag.tag_id);
          }}
          className="ml-1 hover:bg-black/10 rounded-full"
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </Badge>
  );
}

// Tags Display (shows tags as pills, max N visible)
export function TagsDisplay({ tags, allTags, maxVisible = 3, small = false }) {
  if (!tags || tags.length === 0) {
    return <span className="text-slate-400 text-xs">No tags</span>;
  }
  
  const tagsMap = {};
  allTags?.forEach(t => { tagsMap[t.tag_id] = t; });
  
  const visibleTags = tags.slice(0, maxVisible);
  const hiddenCount = tags.length - maxVisible;
  
  return (
    <div className="flex flex-wrap gap-1 items-center">
      {visibleTags.map(tagId => {
        const tag = tagsMap[tagId];
        return tag ? (
          <TagBadge key={tagId} tag={tag} small={small} />
        ) : null;
      })}
      {hiddenCount > 0 && (
        <Badge variant="secondary" className={`${small ? 'text-[10px] px-1' : 'text-xs px-1.5'}`}>
          +{hiddenCount}
        </Badge>
      )}
    </div>
  );
}

// Tag Editor Component (multi-select dropdown)
export function TagEditor({ 
  leadId, 
  currentTags = [], 
  allTags = [], 
  onTagsUpdated,
  disabled = false,
  canCreateTag = false 
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState(TAG_COLORS[0]);
  
  const selectedTagIds = new Set(currentTags);
  
  // Filter tags by search
  const filteredTags = allTags.filter(tag => 
    tag.is_active && tag.name.toLowerCase().includes(search.toLowerCase())
  );
  
  const handleToggleTag = async (tagId) => {
    if (disabled || loading) return;
    
    setLoading(true);
    const isAdding = !selectedTagIds.has(tagId);
    
    try {
      const response = await fetch(`${API_URL}/api/leads/${leadId}/tags`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          add: isAdding ? [tagId] : [],
          remove: isAdding ? [] : [tagId]
        })
      });
      
      if (response.ok) {
        const updatedLead = await response.json();
        onTagsUpdated?.(updatedLead.tags || []);
        toast.success(isAdding ? 'Tag added' : 'Tag removed');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update tags');
      }
    } catch (error) {
      logger.error('Error updating tags:', error);
      toast.error('Failed to update tags');
    } finally {
      setLoading(false);
    }
  };
  
  const handleCreateTag = async () => {
    if (!newTagName.trim()) return;
    
    setCreating(true);
    try {
      const response = await fetch(`${API_URL}/api/tags`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: newTagName.trim(),
          color: newTagColor
        })
      });
      
      if (response.ok) {
        const newTag = await response.json();
        toast.success(`Tag "${newTag.name}" created`);
        setNewTagName('');
        // Trigger refresh of tags list
        window.dispatchEvent(new CustomEvent('tags-updated'));
        // Automatically add the new tag to the lead
        await handleToggleTag(newTag.tag_id);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create tag');
      }
    } catch (error) {
      logger.error('Error creating tag:', error);
      toast.error('Failed to create tag');
    } finally {
      setCreating(false);
    }
  };
  
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={`flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded px-1.5 py-0.5 transition-colors ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
          disabled={disabled}
          data-testid={`tag-editor-${leadId}`}
        >
          <TagIcon className="h-3 w-3" />
          {currentTags.length > 0 ? `${currentTags.length} tag${currentTags.length > 1 ? 's' : ''}` : 'Add tags'}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-2" align="start">
        <div className="space-y-2">
          <Input
            placeholder="Search tags..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 text-sm"
          />
          
          <div className="max-h-48 overflow-y-auto space-y-1">
            {filteredTags.length === 0 ? (
              <p className="text-xs text-slate-500 text-center py-2">
                {search ? 'No matching tags' : 'No tags available'}
              </p>
            ) : (
              filteredTags.map(tag => (
                <button
                  key={tag.tag_id}
                  onClick={() => handleToggleTag(tag.tag_id)}
                  disabled={loading}
                  className="w-full flex items-center justify-between px-2 py-1.5 rounded hover:bg-slate-100 transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: tag.color }}
                    />
                    <span className="text-sm">{tag.name}</span>
                  </div>
                  {selectedTagIds.has(tag.tag_id) && (
                    <Check className="h-4 w-4 text-green-500" />
                  )}
                </button>
              ))
            )}
          </div>
          
          {canCreateTag && (
            <div className="border-t pt-2 mt-2">
              <p className="text-xs text-slate-500 mb-2">Create new tag</p>
              <div className="flex gap-1">
                <Input
                  placeholder="Tag name"
                  value={newTagName}
                  onChange={(e) => setNewTagName(e.target.value)}
                  className="h-7 text-xs flex-1"
                />
                <Popover>
                  <PopoverTrigger asChild>
                    <button 
                      className="w-7 h-7 rounded border flex items-center justify-center"
                      style={{ backgroundColor: newTagColor }}
                    />
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-2">
                    <div className="grid grid-cols-5 gap-1">
                      {TAG_COLORS.map(color => (
                        <button
                          key={color}
                          onClick={() => setNewTagColor(color)}
                          className={`w-6 h-6 rounded ${newTagColor === color ? 'ring-2 ring-offset-1 ring-slate-400' : ''}`}
                          style={{ backgroundColor: color }}
                        />
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
                <Button 
                  size="sm" 
                  className="h-7 px-2"
                  onClick={handleCreateTag}
                  disabled={creating || !newTagName.trim()}
                >
                  {creating ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
                </Button>
              </div>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Tag Filter Component (multi-select for filtering leads)
export function TagFilter({ selectedTags, allTags, onSelectionChange, className }) {
  const [open, setOpen] = useState(false);
  
  const activeTags = allTags.filter(t => t.is_active);
  
  const handleToggle = (tagId) => {
    if (selectedTags.includes(tagId)) {
      onSelectionChange(selectedTags.filter(id => id !== tagId));
    } else {
      onSelectionChange([...selectedTags, tagId]);
    }
  };
  
  const clearAll = () => {
    onSelectionChange([]);
  };
  
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className={`h-7 text-xs px-2 ${className || ''}`} data-testid="tag-filter">
          <TagIcon className="h-3 w-3 mr-1" />
          {selectedTags.length > 0 ? `${selectedTags.length} tag${selectedTags.length > 1 ? 's' : ''}` : 'Tags'}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-2" align="start">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium">Filter by tags</span>
            {selectedTags.length > 0 && (
              <Button variant="ghost" size="sm" className="h-5 text-xs px-1" onClick={clearAll}>
                Clear
              </Button>
            )}
          </div>
          
          <div className="max-h-48 overflow-y-auto space-y-0.5">
            {activeTags.length === 0 ? (
              <p className="text-xs text-slate-500 text-center py-2">No tags available</p>
            ) : (
              activeTags.map(tag => (
                <button
                  key={tag.tag_id}
                  onClick={() => handleToggle(tag.tag_id)}
                  className="w-full flex items-center justify-between px-2 py-1 rounded hover:bg-slate-100 transition-colors"
                >
                  <div className="flex items-center gap-1.5">
                    <div 
                      className="w-2.5 h-2.5 rounded-full" 
                      style={{ backgroundColor: tag.color }}
                    />
                    <span className="text-xs">{tag.name}</span>
                  </div>
                  {selectedTags.includes(tag.tag_id) && (
                    <Check className="h-3 w-3 text-green-500" />
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export { TAG_COLORS };
